import { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Footer from "../components/Footer";
import AccountLink from "../components/AccountLink";
import CountrySwitcher from "../components/CountrySwitcher";
import StyleHubModal from "../components/StyleHubModal";
import TestimonialsSection from "../components/TestimonialsSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// The only real system output we have at launch, used for the hero + before/after.
// Gallery items are loaded dynamically from /api/gallery so this page shows only authentic work.
const DEFAULT_HERO_PORTRAIT = `${BACKEND_URL}/api/files/petart/generated/85da04010d37470b8941a147705628f7_framed_v2.jpg`;
const DEFAULT_BEFORE_SOURCE =
  "https://images.unsplash.com/photo-1561037404-61cd46aa615b?w=900&q=70";
const DEFAULT_AFTER_PORTRAIT = `${BACKEND_URL}/api/files/petart/generated/85da04010d37470b8941a147705628f7_framed_v2.jpg`;
const DEFAULT_RENAISSANCE_THUMB =
  "https://customer-assets.emergentagent.com/job_styled-pet-prints/artifacts/492v7lsa_ChatGPT%20Image%20May%2013%2C%202026%2C%2012_07_49%20PM.png";

// IPL team brand colors — used to render a colored placeholder card until
// the admin curates a real thumbnail in /admin → Styles.
const IPL_BRAND = {
  ipl_mi: { primary: "#004B8D", secondary: "#D5AC32" },
  ipl_csk: { primary: "#FDB913", secondary: "#003366" },
  ipl_rcb: { primary: "#D5152C", secondary: "#101010" },
  ipl_kkr: { primary: "#3A225D", secondary: "#D5AC32" },
  ipl_dc: { primary: "#17449B", secondary: "#D71920" },
  ipl_srh: { primary: "#F26522", secondary: "#101010" },
  ipl_rr: { primary: "#EA1A8C", secondary: "#1F4FA3" },
  ipl_pbks: { primary: "#A71930", secondary: "#C4C8CB" },
  ipl_gt: { primary: "#0F2C46", secondary: "#B4975A" },
  ipl_lsg: { primary: "#003F87", secondary: "#1FB6A1" },
};

const HomePage = () => {
  const [gallery, setGallery] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [photoBookContent, setPhotoBookContent] = useState(null);
  const [homepageSettings, setHomepageSettings] = useState(null);
  const firstCollectionRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((res) => setGallery(res.data.items || []))
      .catch(() => setGallery([]));
    axios
      .get(`${API}/site-settings`)
      .then((res) => setSiteSettings(res.data || {}))
      .catch(() => setSiteSettings({}));
    axios
      .get(`${API}/styles/catalog`)
      .then((res) => setCatalog(res.data.collections || []))
      .catch(() => setCatalog([]));
    axios
      .get(`${API}/photo-books/examples`)
      .then((res) => setPhotoBookContent(res.data || {}))
      .catch(() => setPhotoBookContent({ items: [] }));
    // Editable hero copy + section order/visibility — admin lives at
    // /admin → Homepage. Defaults are generic ("a piece you'll cherish")
    // because we ship multiple products, not just frames.
    axios
      .get(`${API}/homepage-settings`)
      .then((res) => setHomepageSettings(res.data || null))
      .catch(() => setHomepageSettings(null));
  }, []);

  const settingsLoading = siteSettings === null;
  const s = siteSettings || {};
  const heroImage = s.hero_image_url || DEFAULT_HERO_PORTRAIT;
  const beforeImage = s.before_image_url || DEFAULT_BEFORE_SOURCE;
  const afterImage = s.after_image_url || DEFAULT_AFTER_PORTRAIT;
  const renaissanceThumb =
    s.renaissance_hero_image_url ||
    s.after_image_url ||
    DEFAULT_RENAISSANCE_THUMB;

  // Resolve which image a card should show. Admin-managed thumbnail wins;
  // otherwise we use the legacy hero / renaissance fallback; for IPL we use
  // a tinted placeholder card built from the team's brand colors.
  const thumbFor = useMemo(() => {
    return (collectionId, style) => {
      if (style.thumbnail_url) return style.thumbnail_url;
      if (collectionId === "minimalist") return heroImage;
      if (collectionId === "renaissance") return renaissanceThumb;
      return null; // IPL — handled by colored placeholder
    };
  }, [heroImage, renaissanceThumb]);

  const [hubOpen, setHubOpen] = useState(false);

  const openHub = (e) => {
    e?.preventDefault();
    setHubOpen(true);
  };

  // Defaults that match the backend so the page can render meaningfully
  // even before the homepage-settings request resolves.
  const DEFAULT_HOMEPAGE_COPY = {
    eyebrow: "Pet portraits & gifts",
    headline:
      "Turn your pet's photo into something personal you'll cherish forever.",
    subheadline:
      "Pick a product or a style below — we'll craft it and deliver it to your door.",
    primary_cta_label: "Get started",
    mobile_cta_label: "Get started",
    final_eyebrow: "Ready to begin?",
    final_headline: "Create something personal for your home.",
    final_subheadline:
      "A custom piece for your pet — designed, crafted, and delivered with care.",
    final_cta_label: "Get started",
  };
  const DEFAULT_SECTION_ORDER = [
    "photo_books",
    "shop_by_product",
    "shop_by_style",
    "how_it_works",
    "testimonials",
    "before_after",
    "gallery",
    "final_cta",
  ];
  const homepageCopy = { ...DEFAULT_HOMEPAGE_COPY, ...(homepageSettings?.copy || {}) };
  const sectionsToRender = (homepageSettings?.sections || DEFAULT_SECTION_ORDER.map((id) => ({ id, visible: true })))
    .filter((s) => s.visible)
    .map((s) => s.id);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header py-5" data-testid="home-header">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 flex items-center justify-between">
          <Link
            to="/"
            className="serif text-2xl tracking-tight text-[#1A1A1A] font-semibold"
            data-testid="home-brand"
          >
            theSnugglePrints
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/photo-books"
              className="hidden sm:inline text-sm text-[#6B6B6B] hover:text-[#1A1A1A]"
              data-testid="header-photo-books-link"
            >
              Photo Books
            </Link>
            <CountrySwitcher className="hidden md:inline-flex" testIdPrefix="home-header-country" />
            <AccountLink testIdPrefix="home-header" />
            <button
              type="button"
              onClick={openHub}
              className="hidden sm:inline-flex btn-accent text-sm py-2.5 px-5"
              data-testid="header-cta"
            >
              {homepageCopy.primary_cta_label || "Get started"}
            </button>
          </div>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero — admin-editable eyebrow / headline / subheadline */}
        <HomeIntro copy={homepageCopy} />

        {sectionsToRender.map((sectionId) => {
          switch (sectionId) {
            case "photo_books":
              return (
                <PhotoBooksCallout key={sectionId} photoBook={photoBookContent} />
              );
            case "shop_by_product":
              return (
                <ShopByProductRow
                  key={sectionId}
                  thumbnailOverrides={siteSettings?.shop_product_thumbnails || {}}
                />
              );
            case "shop_by_style":
              return (
                <ShopByStyles
                  key={sectionId}
                  collections={catalog}
                  loading={catalog === null || settingsLoading}
                  thumbFor={thumbFor}
                  firstRef={firstCollectionRef}
                />
              );
            case "how_it_works":
              return <HowItWorks key={sectionId} />;
            case "testimonials":
              return <TestimonialsSection key={sectionId} />;
            case "before_after":
              return (
                <BeforeAfter
                  key={sectionId}
                  beforeImage={beforeImage}
                  afterImage={afterImage}
                />
              );
            case "gallery":
              return (
                <Gallery
                  key={sectionId}
                  items={gallery}
                  photoBookGlimpses={photoBookContent?.items || []}
                />
              );
            case "final_cta":
              return (
                <FinalCTA key={sectionId} onClick={openHub} copy={homepageCopy} />
              );
            default:
              return null;
          }
        })}
      </main>

      <Footer />

      <div className="h-20 lg:hidden" aria-hidden="true"></div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-4 pt-3 pb-4 bg-background/95 backdrop-blur-sm border-t border-[#EAE5DF]">
        <button
          type="button"
          onClick={openHub}
          className="btn-accent w-full py-3 text-sm"
          data-testid="mobile-sticky-cta"
        >
          {homepageCopy.mobile_cta_label || "Get started"}
        </button>
        <div className="flex items-center justify-center mt-2">
          <AccountLink testIdPrefix="mobile-sticky" />
        </div>
      </div>

      <StyleHubModal
        open={hubOpen}
        onClose={() => setHubOpen(false)}
        catalog={catalog}
        siteSettings={siteSettings}
      />
    </div>
  );
};

/* ---- Sub-components ---- */

const HomeIntro = ({ copy = {} }) => (
  <section
    className="max-w-6xl mx-auto px-6 lg:px-10 pt-8 pb-12 md:pt-12 md:pb-16 text-center"
    data-testid="hero-section"
  >
    {copy.eyebrow && (
      <p className="label-uppercase mb-4" data-testid="hero-eyebrow">
        {copy.eyebrow}
      </p>
    )}
    <h1
      className="serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] leading-[1.1] mb-6 max-w-3xl mx-auto"
      data-testid="hero-headline"
    >
      {copy.headline}
    </h1>
    {copy.subheadline && (
      <p
        className="text-[#6B6B6B] text-lg max-w-2xl mx-auto whitespace-pre-line"
        data-testid="hero-subheadline"
      >
        {copy.subheadline}
      </p>
    )}
  </section>
);

// Shop by Product — a 4-card row on the homepage. Each card routes to
// /shop/{product_type}. Fetches the live products list so we only render
// cards for product types that have at least one active product. The card
// image can be overridden per product type from /admin → Homepage; when
// no override is set we fall back to the first mockup of the default
// variant.
const PRODUCT_HINTS = {
  frame:   { title: "Frame",         blurb: "Premium framed prints",  icon: "M4 4h16v16H4z" },
  canvas:  { title: "Canvas",        blurb: "Gallery-wrap canvas",    icon: "M3 3h18v18H3z" },
  acrylic: { title: "Acrylic Stand", blurb: "Free-standing display", icon: "M5 3h14v18H5z" },
  mug:     { title: "Coffee Mug",    blurb: "11 oz ceramic mug",      icon: "M6 8h10v9a3 3 0 01-3 3H9a3 3 0 01-3-3V8z" },
};

const ShopByProductRow = ({ thumbnailOverrides = {} }) => {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const ctrl = new AbortController();
    axios
      .get(`${API}/sizes`, { signal: ctrl.signal })
      .then((r) => setProducts(r.data?.items || []))
      .catch(() => setProducts([]));
    return () => ctrl.abort();
  }, []);

  // For each product_type: pick the admin-set override if any, else fall
  // back to the representative variant's first mockup.
  const types = useMemo(() => {
    const grouped = {};
    (products || []).forEach((p) => {
      if (!p.product_type) return;
      (grouped[p.product_type] = grouped[p.product_type] || []).push(p);
    });
    return Object.keys(grouped).map((t) => {
      const variants = grouped[t];
      const def = variants.find((v) => v.is_default) || variants[0];
      const fallback =
        (def?.mockups && def.mockups[0]?.url) ||
        def?.frame_image_url ||
        def?.mug_reference_image_url ||
        null;
      const heroImage = thumbnailOverrides[t] || fallback;
      return { type: t, heroImage };
    });
  }, [products, thumbnailOverrides]);

  if (types.length === 0) return null;

  return (
    <section
      className="max-w-6xl mx-auto px-6 lg:px-10 pb-12"
      data-testid="home-shop-by-product"
    >
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="serif text-2xl sm:text-3xl text-[#1A1A1A]">
          Shop by Product
        </h2>
        <p className="text-xs label-uppercase text-[#6B6B6B] hidden sm:block">
          Looking for something specific?
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {types.map(({ type: t, heroImage }) => {
          const meta = PRODUCT_HINTS[t] || { title: t, blurb: "" };
          return (
            <Link
              key={t}
              to={`/shop/${t}`}
              className="bg-card border border-[#EAE5DF] hover:border-[#C8A27A] transition-colors flex flex-col group overflow-hidden"
              data-testid={`home-shop-product-${t}`}
            >
              <div className="aspect-square bg-[#F3EEE6] flex items-center justify-center overflow-hidden">
                {heroImage ? (
                  <img
                    src={heroImage}
                    alt={meta.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                  />
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    className="w-12 h-12 text-[#C8A27A]"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.25"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d={meta.icon} />
                  </svg>
                )}
              </div>
              <div className="p-4">
                <p className="serif text-lg text-[#1A1A1A] group-hover:text-[#9B6A2D] transition-colors">
                  {meta.title}
                </p>
                <p className="text-xs text-[#6B6B6B] mt-1">{meta.blurb}</p>
                <span className="text-xs text-[#9B6A2D] mt-3 inline-flex items-center gap-1">
                  Shop {meta.title.toLowerCase()} →
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
};

// Photo Books callout — hand-designed product line. Admin controls the
// image, headline, and subheadline from /admin → Photo Books → Settings.
const PhotoBooksCallout = ({ photoBook }) => {
  const heroImage = photoBook?.hero_image_url || "";
  const headline = photoBook?.headline || "Custom Pet Photo Books";
  const subheadline =
    photoBook?.subheadline ||
    "Premium hand-designed photo books, crafted from your memories — by our team, with care for every page.";
  return (
    <section
      className="max-w-6xl mx-auto px-6 lg:px-10 pt-2 pb-12"
      data-testid="home-photo-books-callout"
    >
      <Link
        to="/photo-books"
        className="block group border border-[#EAE5DF] hover:border-[#C8A27A] transition-colors"
      >
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr]">
          <div className="aspect-video md:aspect-auto bg-[#FAF6EF] flex items-center justify-center overflow-hidden">
            {heroImage ? (
              <img
                src={heroImage}
                alt={headline}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
                data-testid="home-photo-books-hero-image"
              />
            ) : (
              <div className="p-10">
                <svg
                  viewBox="0 0 120 90"
                  className="w-32 h-24 text-[#C8A27A]"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="10" y="14" width="46" height="62" rx="2" />
                  <rect x="64" y="14" width="46" height="62" rx="2" />
                  <line x1="10" y1="76" x2="110" y2="76" />
                  <line x1="60" y1="14" x2="60" y2="76" />
                </svg>
              </div>
            )}
          </div>
          <div className="p-7 sm:p-10">
            <p className="label-uppercase text-[#6B6B6B] mb-2">New product line</p>
            <h2
              className="serif text-2xl sm:text-3xl text-[#1A1A1A] mb-3 group-hover:text-[#9B6A2D] transition-colors"
              data-testid="home-photo-books-headline"
            >
              {headline}
            </h2>
            <p
              className="text-[#6B6B6B] mb-4 max-w-md whitespace-pre-line"
              data-testid="home-photo-books-subheadline"
            >
              {subheadline}
            </p>
            <span className="inline-flex items-center gap-1 text-sm font-medium text-[#9B6A2D]">
              Explore Photo Books →
            </span>
          </div>
        </div>
      </Link>
    </section>
  );
};

// Wraps StyleCollections with a "Shop by Style" section heading so the
// grouping of Modern / Renaissance / IPL collections reads as a single
// shopping entry point — symmetrical with Shop by Product above it.
const ShopByStyles = ({ collections, loading, thumbFor, firstRef }) => {
  if (loading && (!collections || collections.length === 0)) {
    return (
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20" data-testid="home-shop-by-style">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="serif text-2xl sm:text-3xl text-[#1A1A1A]">Shop by Style</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 border-2 border-[#C8A27A] border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }
  if (!collections || collections.length === 0) return null;
  return (
    <div data-testid="home-shop-by-style">
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-2 pt-4">
        <div className="flex items-baseline justify-between">
          <h2 className="serif text-2xl sm:text-3xl text-[#1A1A1A]">Shop by Style</h2>
          <p className="text-xs label-uppercase text-[#6B6B6B] hidden sm:block">
            Find your aesthetic
          </p>
        </div>
      </section>
      <StyleCollections
        collections={collections}
        loading={false}
        thumbFor={thumbFor}
        firstRef={firstRef}
      />
    </div>
  );
};

const StyleCollections = ({ collections, loading, thumbFor, firstRef }) => {
  if (loading && (!collections || collections.length === 0)) {
    return (
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20">
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 border-2 border-[#C8A27A] border-t-transparent rounded-full animate-spin" />
        </div>
      </section>
    );
  }
  if (!collections || collections.length === 0) return null;
  return (
    <div data-testid="style-collections">
      {collections.map((c, idx) => (
        <CollectionRow
          key={c.id}
          collection={c}
          thumbFor={thumbFor}
          attachRef={idx === 0 ? firstRef : null}
        />
      ))}
    </div>
  );
};

const CollectionRow = ({ collection, thumbFor, attachRef }) => {
  const scrollerRef = useRef(null);
  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.8) * (dir === "left" ? -1 : 1);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section
      ref={attachRef}
      className="max-w-6xl mx-auto px-6 lg:px-10 pb-12 md:pb-16"
      data-testid={`collection-row-${collection.id}`}
    >
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="serif text-2xl sm:text-3xl text-[#1A1A1A]">
            {collection.name}
          </h2>
          {collection.description && (
            <p className="text-sm text-[#6B6B6B] mt-1">{collection.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs label-uppercase text-[#6B6B6B] hidden sm:inline">
            {collection.styles.length}{" "}
            {collection.styles.length === 1 ? "style" : "styles"}
          </span>
          {/* Desktop scroll controls — keep the carousel single-row no matter
              how many styles a collection has. */}
          <div className="hidden md:flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollBy("left")}
              aria-label={`Scroll ${collection.name} left`}
              className="w-8 h-8 border border-[#EAE5DF] hover:border-[#C8A27A] hover:bg-[#FAF6EF] transition-colors flex items-center justify-center"
              data-testid={`collection-scroll-left-${collection.id}`}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1A1A1A]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            <button
              type="button"
              onClick={() => scrollBy("right")}
              aria-label={`Scroll ${collection.name} right`}
              className="w-8 h-8 border border-[#EAE5DF] hover:border-[#C8A27A] hover:bg-[#FAF6EF] transition-colors flex items-center justify-center"
              data-testid={`collection-scroll-right-${collection.id}`}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#1A1A1A]" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
        </div>
      </div>

      {/* Strict horizontal carousel — no wrap to a second row at any
          breakpoint. Each card has a fixed responsive width; the row scrolls
          horizontally with scroll-snap so each card lands neatly aligned. */}
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto scroll-snap-x -mx-6 px-6 md:mx-0 md:px-0 pb-2 scrollbar-thin"
        style={{ scrollSnapType: "x mandatory" }}
        data-testid={`collection-carousel-${collection.id}`}
      >
        {collection.styles.map((s) => (
          <StyleCard
            key={s.id}
            style={s}
            collectionId={collection.id}
            thumbUrl={thumbFor(collection.id, s)}
          />
        ))}
      </div>
    </section>
  );
};

const StyleCard = ({ style, collectionId, thumbUrl }) => {
  const isIpl = collectionId === "ipl";
  const brand = IPL_BRAND[style.id];
  const comingSoon = !!style.coming_soon;
  // When coming soon, prefer admin-uploaded image over the regular thumbnail
  const effectiveThumb = comingSoon && style.coming_soon_image_url
    ? style.coming_soon_image_url
    : thumbUrl;
  const cardClasses =
    "group flex-shrink-0 w-48 sm:w-56 md:w-60 lg:w-64 bg-card border border-[#EAE5DF] transition-colors flex flex-col scroll-snap-start";
  const innerVisual = (
    <div className="bg-[#F3EEE6] aspect-[3/4] overflow-hidden flex items-center justify-center relative">
      {effectiveThumb ? (
        <img
          src={effectiveThumb}
          alt={style.name}
          className={`w-full h-full object-cover transition-transform duration-500 ${
            comingSoon ? "" : "group-hover:scale-105"
          }`}
          loading="lazy"
        />
      ) : isIpl && brand ? (
        <div
          className="w-full h-full flex items-center justify-center"
          style={{
            background: `linear-gradient(135deg, ${brand.primary} 0%, ${brand.secondary} 100%)`,
          }}
        >
          <span className="serif text-3xl text-white drop-shadow-lg tracking-wide text-center px-3">
            {style.name}
          </span>
        </div>
      ) : (
        <div className="w-full h-full bg-[#E5DED1]" />
      )}
      {comingSoon && (
        <div className="absolute inset-0 bg-black/45 flex flex-col items-center justify-center pointer-events-none">
          <span className="label-uppercase text-white text-xs tracking-widest mb-1">
            Coming
          </span>
          <span className="serif text-white text-2xl">Soon</span>
        </div>
      )}
    </div>
  );
  const innerLabel = (
    <div className="px-3 py-3">
      <h3 className="font-medium text-sm text-[#1A1A1A] truncate">
        {style.name}
      </h3>
    </div>
  );

  if (comingSoon) {
    return (
      <div
        className={`${cardClasses} cursor-not-allowed opacity-90`}
        data-testid={`style-card-${style.id}`}
        aria-disabled="true"
      >
        {innerVisual}
        {innerLabel}
      </div>
    );
  }

  return (
    <Link
      to={`/style/${style.slug || style.id}`}
      className={`${cardClasses} hover:border-[#C8A27A]`}
      data-testid={`style-card-${style.id}`}
    >
      {innerVisual}
      {innerLabel}
    </Link>
  );
};

const HowItWorks = () => {
  const steps = [
    {
      n: "01",
      title: "Upload your pet's photo",
      body:
        "A clear, well-lit photo of your pet works best. One photo is all we need.",
    },
    {
      n: "02",
      title: "We craft your portrait",
      body:
        "Your pet's likeness is carefully painted in the style you've chosen, ready to admire.",
    },
    {
      n: "03",
      title: "Order your framed print",
      body:
        "Review your portrait and place your order. We print on premium canvas and deliver the framed piece to your door.",
    },
  ];

  return (
    <section
      className="border-t border-[#EAE5DF]"
      data-testid="how-it-works-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28">
        <div className="max-w-2xl mb-16">
          <p className="label-uppercase mb-4">How it works</p>
          <h2 className="serif text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A]">
            Three quiet steps from photo to a portrait on your wall.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16">
          {steps.map((s) => (
            <div key={s.n}>
              <div className="serif text-5xl text-[#C8A27A] mb-6">{s.n}</div>
              <h3 className="serif text-2xl text-[#1A1A1A] mb-4">{s.title}</h3>
              <p className="text-[#6B6B6B]">{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BeforeAfter = ({ beforeImage, afterImage }) => (
  <section
    className="border-t border-[#EAE5DF] bg-[#F3EEE6]"
    data-testid="before-after-section"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28">
      <div className="max-w-2xl mb-16">
        <p className="label-uppercase mb-4">From your photo</p>
        <h2 className="serif text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A]">
          From your photo to a portrait made for your wall.
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
        <figure>
          <div className="bg-white border border-[#EAE5DF] p-4">
            <img
              src={beforeImage}
              alt="Original pet photo"
              className="w-full h-[520px] object-cover"
              data-testid="before-image"
            />
          </div>
          <figcaption className="mt-4 label-uppercase text-center">
            Before: your photo
          </figcaption>
        </figure>

        <figure>
          <div className="bg-white border border-[#EAE5DF] p-4">
            <img
              src={afterImage}
              alt="Final custom pet portrait"
              className="w-full h-[520px] object-contain bg-white"
              data-testid="after-image"
            />
          </div>
          <figcaption className="mt-4 label-uppercase text-center">
            After: your portrait
          </figcaption>
        </figure>
      </div>
    </div>
  </section>
);

const Gallery = ({ items, photoBookGlimpses = [] }) => {
  const hasItems = items && items.length > 0;
  // Show up to 4 photo book thumbs as a glimpse strip. Filter to images so
  // the strip stays visually consistent on the homepage.
  const pbGlimpses = (photoBookGlimpses || [])
    .filter((it) => it.media_type !== "video" && it.image_url)
    .slice(0, 4);

  return (
    <section
      className="border-t border-[#EAE5DF]"
      data-testid="gallery-section"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 md:py-28">
        <div className="max-w-2xl mb-16">
          <p className="label-uppercase mb-4">Recent portraits</p>
          <h2 className="serif text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A]">
            Portraits crafted with personality, elegance, and timeless charm.
          </h2>
        </div>

        {hasItems ? (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8"
            data-testid="gallery-grid"
          >
            {items.map((it, i) => (
              <figure
                key={i}
                className="bg-white border border-[#EAE5DF] p-3"
              >
                <img
                  src={it.preview_url}
                  alt="Pet portrait"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </figure>
            ))}
          </div>
        ) : (
          <div
            className="border border-dashed border-[#EAE5DF] py-20 px-8 text-center bg-white"
            data-testid="gallery-empty"
          >
            <p className="serif text-2xl text-[#1A1A1A] mb-3">
              Yours could be the first portrait here.
            </p>
            <p className="text-[#6B6B6B] max-w-md mx-auto">
              As pet parents create portraits of their companions, the pieces
              they love most will quietly appear in this gallery.
            </p>
          </div>
        )}

        {pbGlimpses.length > 0 && (
          <div className="mt-16 pt-12 border-t border-[#EAE5DF]" data-testid="gallery-photo-book-glimpses">
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
              <div>
                <p className="label-uppercase mb-1">Also from our team</p>
                <h3 className="serif text-2xl sm:text-3xl text-[#1A1A1A]">
                  Glimpses of our Photo Books
                </h3>
              </div>
              <Link
                to="/photo-books"
                className="text-sm text-[#9B6A2D] hover:underline"
                data-testid="gallery-photo-book-cta"
              >
                Explore Photo Books →
              </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {pbGlimpses.map((it) => (
                <Link
                  to="/photo-books"
                  key={it.id}
                  className="aspect-square bg-card border border-[#EAE5DF] overflow-hidden group"
                  data-testid={`gallery-photo-book-glimpse-${it.id}`}
                >
                  <img
                    src={it.image_url}
                    alt={it.caption || "Photo book preview"}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                  />
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

const FinalCTA = ({ onClick, copy = {} }) => (
  <section
    className="border-t border-[#EAE5DF]"
    data-testid="final-cta-section"
  >
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-center">
      {copy.final_eyebrow && (
        <p className="label-uppercase mb-4" data-testid="final-cta-eyebrow">
          {copy.final_eyebrow}
        </p>
      )}
      <h2
        className="serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mb-6"
        data-testid="final-cta-headline"
      >
        {copy.final_headline || "Create something personal for your home."}
      </h2>
      {copy.final_subheadline && (
        <p
          className="text-[#6B6B6B] text-lg mb-10 max-w-xl mx-auto whitespace-pre-line"
          data-testid="final-cta-subheadline"
        >
          {copy.final_subheadline}
        </p>
      )}
      <button
        type="button"
        onClick={onClick}
        className="btn-accent"
        data-testid="final-cta"
      >
        {copy.final_cta_label || "Get started"}
      </button>
    </div>
  </section>
);

export default HomePage;
